import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Language } from '../types';
import { languageFromPath } from '../utils/seo';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const getPreferredBrowserLanguage = (): Language => {
  const raw = navigator.language.toLowerCase();
  if (raw.startsWith('en')) return 'en';
  if (raw.startsWith('es')) return 'es';
  return 'fr';
};

const stripLocalePrefix = (pathname: string): string => {
  if (pathname.startsWith('/en/')) return pathname.slice(3);
  if (pathname.startsWith('/es/')) return pathname.slice(3);
  if (pathname === '/en' || pathname === '/es') return '/';
  return pathname;
};

const isPrerenderSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('prerender');
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguageState] = useState<Language>(() => {
    if (location.pathname.startsWith('/en') || location.pathname.startsWith('/es')) {
      return languageFromPath(location.pathname);
    }
    if (isPrerenderSession()) {
      return 'fr';
    }
    return getPreferredBrowserLanguage();
  });

  useEffect(() => {
    if (isPrerenderSession()) {
      setLanguageState(languageFromPath(location.pathname));
      return;
    }

    const { pathname } = location;
    const hasLanguagePrefix = pathname.startsWith('/en') || pathname.startsWith('/es');

    if (hasLanguagePrefix) {
      setLanguageState(languageFromPath(pathname));
      return;
    }

    const preferred = getPreferredBrowserLanguage();
    setLanguageState(preferred);

    if (preferred === 'fr') return;

    const basePath = stripLocalePrefix(pathname);
    const newPath = basePath === '/'
      ? `/${preferred}`
      : `/${preferred}${basePath}`;

    navigate(newPath, { replace: true });
  }, [location, navigate]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    const basePath = stripLocalePrefix(location.pathname);

    let newPath: string;
    if (lang === 'fr') {
      newPath = basePath;
    } else {
      newPath = basePath === '/' ? `/${lang}` : `/${lang}${basePath}`;
    }

    navigate(newPath);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
